import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, AlertCircle } from 'lucide-react';
import { businessInfo } from '../mock';
import OverlayLetterheadRenderer from '../components/OverlayLetterheadRenderer';
import StructuredInvoiceView from '../components/StructuredInvoiceView';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  paid: { label: 'PAID', color: 'text-green-700 border-green-700' },
  partial: { label: 'PARTIAL', color: 'text-amber-700 border-amber-700' },
  unpaid: { label: 'UNPAID', color: 'text-red-700 border-red-700' },
};

const InvoiceView = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.title;
    document.title = invoice ? `Invoice ${invoice.invoice_number} — TayLord Arms LLC` : 'Invoice — TayLord Arms LLC';
    return () => { document.title = prev; };
  }, [invoice]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/invoices/public/${id}`);
        if (!cancelled) setInvoice(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500" data-testid="invoice-loading">Loading invoice…</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="invoice-not-found">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-900">Invoice not found</h1>
          <p className="text-slate-600 mt-2">This invoice may have been removed or the link is incorrect. Please contact TayLord Arms LLC if you believe this is an error.</p>
          <p className="text-slate-500 text-sm mt-4">{businessInfo.phone} · {businessInfo.email}</p>
        </div>
      </div>
    );
  }

  const stamp = STATUS_BADGE[invoice.payment_status] || STATUS_BADGE.unpaid;

  // ===== OVERLAY MODE: render the uploaded form as the full page with overlay fields =====
  if (invoice.letterhead?.template_mode === 'overlay' && invoice.letterhead?.image_base64) {
    return (
      <div className="min-h-screen bg-slate-100 py-8 print:py-0 print:bg-white">
        <style>{`
          @media print {
            @page { size: letter; margin: 0; }
            body { background: white !important; }
            .print-hide { display: none !important; }
            .overlay-page-wrap { padding: 0 !important; }
            .overlay-page-wrap > div { box-shadow: none !important; border: none !important; max-width: 100% !important; }
          }
        `}</style>
        <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between print-hide">
          <div className="text-sm text-slate-600">
            Invoice <span className="font-mono font-bold text-slate-900">{invoice.invoice_number}</span>
            <span className={`ml-3 inline-block px-2 py-0.5 border-2 font-bold tracking-widest text-xs ${stamp.color}`}>{stamp.label}</span>
          </div>
          <Button
            data-testid="invoice-print"
            onClick={() => window.print()}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
        <div className="overlay-page-wrap px-4">
          <OverlayLetterheadRenderer
            letterhead={invoice.letterhead}
            doc={invoice}
            testid="invoice-overlay-render"
          />
        </div>
      </div>
    );
  }

  // ===== STRUCTURED LAYOUT (default for ALL invoices) =====
  // Older invoices without explicit layout='structured' also render in this design;
  // missing fields (firearm, service_description, etc.) just show as empty lines on the form.
  return (
    <div className="min-h-screen bg-slate-100 py-6 print:py-0 print:bg-white">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.3in; }
          body { background: white !important; }
          .print-hide { display: none !important; }
        }
      `}</style>
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print-hide">
        <div className="text-sm text-slate-600">
          Invoice <span className="font-mono font-bold text-slate-900">{invoice.invoice_number}</span>
          <span className={`ml-3 inline-block px-2 py-0.5 border-2 font-bold tracking-widest text-xs ${stamp.color}`}>{stamp.label}</span>
        </div>
        <Button data-testid="invoice-print" onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>
      <StructuredInvoiceView invoice={invoice} />
    </div>
  );
};

export default InvoiceView;
